import React from 'react';
import styled from 'styled-components';
import { combineImpactSummaryAndDetails, ImpactReceipt } from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { ReportAnswers } from '../FeedbackDashboard/Feedback/FeedbackReport';
import { Investment } from 'npo/pages/DonationsBeta/api';
import { totalAllocatedDonations } from 'npo/pages/DonationsBeta/DonationDetails/DonationWhereContent';
import { getBudgetFromAnswers } from '../FeedbackDashboard/Feedback/FeedbackImpactReceipt/ReportBudget';
import { numberWithCommas } from 'shared/utils/numberUtils';
import TrueImpactCircleProgress from 'shared/design-system/components/TrueImpactCircleProgress';
import { parseImpactDetailsAnswer } from '../FeedbackDashboard/Feedback/FeedbackImpactDetails';
import {
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
} from 'npo/pages/NPOFeedback/utils';
import { parseImpactSummaryAnswer } from 'npo/pages/NPOFeedback/FeedbackDetails/ImpactSummaryDetails';

const getPercentageValue = (percentage: number): string | number => {
  if (!percentage || percentage < 0) {
    return 0;
  }
  if (percentage > 100) {
    return 100;
  }
  const p = percentage.toFixed(2);
  return p.split('.00')[0];
};

const getIndicatorTitle = (
  title: string,
  forecast: string | number,
  percentage: number,
  tier: ImpactReceipt['allocation']['tier'],
): JSX.Element | string => {
  const num =
    (typeof forecast === 'number'
      ? forecast
      : Number(forecast.replace(/,/g, ''))) || 0;
  const finalNumbers =
    tier === 'Foundational' ? num : Math.ceil((Math.min(percentage, 100) / 100) * num);
  return (
    <>
      <strong>{numberWithCommas(finalNumbers)}</strong> {title}
    </>
  );
};

interface DonorClaimProps {
  ir: ImpactReceipt;
  answers: ReportAnswers;
  investment: Investment;
  isLegacy?: boolean;
}

const StyledDiv = styled.div`
  padding-bottom: 22px;
  border-bottom: 6px solid ${COLORS.highlight};
  background-color: ${COLORS.highlightBackground};
  .header {
    margin: 0;
    background-color: ${COLORS.highlight};
    padding: ${SPACERS.small} ${SPACERS.extraLarge};
  }
  .organization-name {
    margin: 0;
    padding: 22px ${SPACERS.extraLarge};
  }
  .portion-contributed {
    border-radius: ${SPACERS.extraTiny};
    border: 1px solid ${COLORS.grayLight};
    background-color: ${COLORS.whiteBase};
    padding: ${SPACERS.medium} ${SPACERS.extraLarge};
    .portion-contributed-heading {
      margin-bottom: ${SPACERS.small};
    }
    .donation-visual {
      .middle-donation-text {
        .middle-donation-heading {
          margin: 0;
          text-align: center;
        }
        .middle-donation-subheading {
          text-align: center;
        }
      }
    }
    .donation-info {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: ${SPACERS.small};
      .donation-visual {
      }
      .donation-details-wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: ${SPACERS.large};
        .donation-detail-heading {
          color: ${COLORS.blackBase};
          margin-bottom: ${SPACERS.extraTiny};
        }
      }
    }
  }
  .donor-claim {
    margin-top: ${SPACERS.medium};
    border-radius: ${SPACERS.extraTiny};
    border: 1px solid ${COLORS.grayLight};
    background-color: ${COLORS.whiteBase};
    .donor-claim-heading {
      margin-bottom: 0;
      padding: ${SPACERS.medium} ${SPACERS.extraLarge};
    }
    .donor-claim-top {
      background-color: ${COLORS.grayLight};
      padding: ${SPACERS.small} ${SPACERS.extraLarge};
      .donor-claim-capsule {
        font-size: 12px;
        line-height: 12px;
        letter-spacing: -0.24px;
        text-transform: uppercase;
      }
    }
    .donor-claim-indicators {
      padding-top: 12px;
      border-top: 1px solid ${COLORS.grayLight};
      padding-bottom: ${SPACERS.small};
      padding-left: ${SPACERS.extraLarge};
      padding-right: ${SPACERS.extraLarge};
      .donor-claim-indicator {
        padding-top: ${SPACERS.tiny};
        margin-bottom: ${SPACERS.tiny};
        padding-bottom: ${SPACERS.tiny};
      }
    }
  }
`;

const DonorClaimIndicator: React.FC<{
  title: string;
  forecast: string | number | undefined;
  percentage: number;
  tier: ImpactReceipt['allocation']['tier'];
  successCriteria: string;
  className?: string;
}> = ({ title, forecast, percentage, tier, successCriteria, className }) => {
  if (!forecast) return null;
  return (
    <div className={className}>
      <TrueImpactText>
        {getIndicatorTitle(title, forecast, percentage, tier)}
      </TrueImpactText>
      <TrueImpactText variant="body-small">{successCriteria}</TrueImpactText>
    </div>
  );
};

const DonorClaim: React.FC<DonorClaimProps> = (
  { ir, investment, answers, isLegacy },
) => {
  let { totalBudget } = getBudgetFromAnswers(answers);

  // LEGACY REPORTS: get budget from report fields (use current total, not initial).
  if (!totalBudget) {
    const currentTotal = ir?.report?.budget?.find(b => b.expenses === 'Total Program Cost')?.current;
    totalBudget = currentTotal ? Number(currentTotal) : Number(ir?.report?.total || 0);
  }

  let { calculatedTotalDonations } = totalAllocatedDonations(investment);

  /* if an allocation ID is set on the url, the back automatically sets
  the invested amount on the ir, showing the actual amount invested on the UI */
  if (ir?.invested) {
    calculatedTotalDonations = ir.invested;
  }

  const percentage =
    !calculatedTotalDonations || !totalBudget
      ? 0
      : (calculatedTotalDonations / totalBudget) * 100;

  const impactSummary = parseImpactSummaryAnswer(
    answers.impact_summary?.find(
      x => x.question.dbId === IMPACT_SUMMARY_QUESTION_ID
    )?.text,
  );
  const impactDetails = parseImpactDetailsAnswer(
    answers.indicator_details?.find(
      x => x.question.dbId === INDICATOR_DETAILS_QUESTION_ID
    )?.text,
  );

  const combined = combineImpactSummaryAndDetails(impactSummary, impactDetails);

  /* const indicators = Object.values(ir.report.impactModel).find(
    ({ name }) => name === 'Succeed'
  ); */

  const tier: ImpactReceipt['allocation']['tier'] =
    ir.allocation.tier === 'Foundational' ? 'Foundational' : 'Significant';

  const modelExists = Boolean(impactDetails) && Boolean(impactSummary);

  return (
    <StyledDiv>
      <TrueImpactText
        color="whiteBase"
        className="header"
        variant="heading-label"
      >
        Donor Impact Receipt
      </TrueImpactText>
      <TrueImpactText variant="heading" className="organization-name">
        {ir.portfolioAccountName}
      </TrueImpactText>
      <div className="portion-contributed">
        <TrueImpactText
          variant="heading-subheading"
          className="portion-contributed-heading"
        >
          Portion Contributed
        </TrueImpactText>
        <div className="donation-info">
          <TrueImpactCircleProgress
            budget={calculatedTotalDonations}
            total={totalBudget}
            className="donation-visual"
            middleComponent={
              <div className="middle-donation-text">
                <TrueImpactText
                  variant="heading"
                  className="middle-donation-heading"
                >
                  {getPercentageValue(percentage)}%
                </TrueImpactText>
                <TrueImpactText
                  variant="body-small"
                  className="middle-donation-subheading"
                >
                  of total budget
                </TrueImpactText>
              </div>
            }
          />
          <div className="donation-details-wrapper">
            <div className="donation-detail">
              <TrueImpactText
                variant="heading-label"
                className="donation-detail-heading"
              >
                Donation
              </TrueImpactText>
              <TrueImpactText>
                ${numberWithCommas(calculatedTotalDonations)}
              </TrueImpactText>
            </div>
            <div className="donation-detail">
              <TrueImpactText
                variant="heading-label"
                className="donation-detail-heading"
              >
                Program Budget
              </TrueImpactText>
              <TrueImpactText>${numberWithCommas(totalBudget)}</TrueImpactText>
            </div>
            <div className="donation-detail">
              <TrueImpactText
                variant="heading-label"
                className="donation-detail-heading"
              >
                Donation Type
              </TrueImpactText>
              <TrueImpactText>{tier}</TrueImpactText>
            </div>
          </div>
        </div>
      </div>
      <div className="donor-claim">
        <TrueImpactText
          variant="heading-subheading"
          className="donor-claim-heading"
        >
          Donor’s Claim
        </TrueImpactText>
        {/* V4 - Non Legacy */}
        {!isLegacy && (
          <div className="donor-claim-indicators">
            {!modelExists ? (
              <TrueImpactText variant="body-small">
                No impact indicators have been defined for this program.
              </TrueImpactText>
            ) : null}
            {combined.succeed.map((indicator, i) => (
              <DonorClaimIndicator
                key={i}
                tier={tier}
                percentage={percentage}
                title={indicator.indicatorName}
                forecast={indicator.finalForecast || indicator.forecast}
                className="donor-claim-indicator"
                successCriteria={indicator.updatedText || indicator.definition}
              />
            ))}
          </div>
        )}
        {/* V3 - Legacy */}
        {isLegacy && (
          <div className="donor-claim-indicators">
            {ir?.legacy?.donorsClaim?.length === 0 && (
              <TrueImpactText variant="body-small">
                No impact indicators have been defined for this program.
              </TrueImpactText>
            )}
            {ir?.legacy?.donorsClaim?.map((indicator, i) => (
              <DonorClaimIndicator
                key={i}
                tier={tier}
                percentage={percentage}
                title={indicator.indicatorName}
                forecast={indicator.forecast || 0}
                className="donor-claim-indicator"
                successCriteria={indicator.successCriteria || '-'}
              />
            ))}
          </div>
        )}
      </div>
    </StyledDiv>
  );
};

export default DonorClaim;
